// frontend/src/components/common/Table.jsx
// COMPONENTE TABLA REUTILIZABLE BASADO EN lista_generica.html
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { spin } from '../../styles/animations';
import { ActionButton as SharedActionButton } from './Buttons';

// CONTENEDOR PRINCIPAL DE LA TABLA CON SCROLL VERTICAL
const TableContainer = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;
  min-height: 0;

  /* Variant "rows": sin recuadro propio, misma superficie que el resto del
     panel — igual que Check List (ver ListaUnificada.jsx). */
  ${({ $variant, theme }) => $variant === 'rows' && `
    background: ${theme.colors.bgPrincipal};
    border: none;
    border-radius: 0;
    box-shadow: none;
  `}
`;

// WRAPPER PARA EL SCROLL DE LA TABLA
const TableScrollWrapper = styled.div`
  flex-grow: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: ${({ $horizontalScroll }) => $horizontalScroll ? 'auto' : 'hidden'};
  /* Altura máxima adaptable; se puede sobrescribir vía prop */
  max-height: ${({ $maxHeight }) => $maxHeight || 'calc((100vh / var(--app-zoom, 1)) - 200px)'};
  padding-bottom: ${({ $noExtraBottomSpace }) => $noExtraBottomSpace ? '0' : '48px'}; /* Evita que el footer tape la última fila */
  /* No agregar margen derecho artificial; dejamos que el ancho de la columna Acciones resuelva el solapamiento */
  padding-right: 0;
  /* En navegadores modernos, reservar canal de scroll de forma estable */
  scrollbar-gutter: stable both-edges;
  
  /* Scrollbar personalizado igual al legacy */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.scrollThumb} ${({ theme }) => theme.colors.scrollTrack};
  
  &::-webkit-scrollbar {
    width: 8px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.scrollTrack};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollThumb};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.scrollThumbHover};
  }
`;

// TABLA PRINCIPAL
const StyledTable = styled.table`
  width: 100%;
  min-width: ${({ $minWidth }) => $minWidth ? `${$minWidth}px` : 'auto'};
  border-collapse: collapse;
  position: relative;
  table-layout: fixed; /* Respetar anchos de columnas definidos */
  /* Ocultar la línea inferior visual en la última fila */
  tbody tr:last-child td { border-bottom: none; }
  /* El ancho de la última columna (Acciones) se maneja vía props de columna */
`;

// ENCABEZADO DE TABLA CON ORDENAMIENTO
const TableHeader = styled.thead`
  position: sticky;
  top: 0;
  z-index: ${({ theme }) => theme.zIndex.sticky};
  background: ${({ theme }) => theme.colors.bgSecundario};

  ${({ $variant, theme }) => $variant === 'rows' && `
    background: ${theme.colors.bgPrincipal};
  `}
`;

const TableHeaderCell = styled.th`
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 12px;
  text-align: ${({ $align }) => $align || 'left'};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.borderDefault};
  cursor: ${({ $sortable }) => $sortable ? 'pointer' : 'default'};
  user-select: none;
  transition: ${({ theme }) => theme.transitions.fast};
  ${({ $width }) => $width ? `width: ${$width}; min-width: ${$width};` : ''}
  position: relative; /* Garantizar referencia para los handles de resize */
  overflow: visible; /* Permitir que el handle sobresalga y sea clickeable */

  &:hover {
    background: ${({ theme, $sortable }) => $sortable ? theme.colors.surfaceHover : theme.colors.bgSecundario};
  }

  /* Icono de ordenamiento */
  ${({ $sortable, $sortDirection, theme }) => $sortable ? `
    padding-right: 40px;
    &::after {
      content: '${$sortDirection === 'asc' ? '▲' : $sortDirection === 'desc' ? '▼' : '⇅'}';
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: ${$sortDirection ? theme.colors.primary : theme.colors.secondary};
      font-size: 10px;
    }
  ` : ''}

  ${({ $variant, theme }) => $variant === 'rows' && `
    background: ${theme.colors.bgPrincipal};
    padding: 6px 10px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-size: 11px;
    font-weight: 700;
    color: ${theme.colors.txtMuted};

    &:hover {
      background: ${theme.colors.bgPrincipal};
    }
  `}
`;

const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: -6px; /* Sacarlo ligeramente fuera del th para ampliar el área de captura */
  width: 14px; /* Área más amplia para facilitar el agarre */
  height: 100%;
  cursor: col-resize;
  z-index: 6;
  background: ${({ theme }) => theme.colors.transparent};
  touch-action: none;

  &:hover {
    background: ${({ theme }) => theme.colors.borderDefault};
    opacity: 0.22;
  }
`;

const LeftResizeHandle = styled.div`
  position: absolute;
  top: 0;
  left: -6px; /* Sacarlo ligeramente fuera del th para ampliar el área de captura */
  width: 14px;
  height: 100%;
  cursor: col-resize;
  z-index: 6;
  background: ${({ theme }) => theme.colors.transparent};
  touch-action: none;

  &:hover {
    background: ${({ theme }) => theme.colors.borderDefault};
    opacity: 0.22;
  }
`;

// CUERPO DE LA TABLA
const TableBody = styled.tbody``;

// Celda del sentinel de render incremental (fila "mostrando X de Y")
const SentinelCell = styled.td`
  padding: 10px 12px;
  text-align: center;
  font-size: 12px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: none;
`;

// FILA DE TABLA CON EFECTOS HOVER Y ACCIONES
const TableRow = styled.tr`
  position: relative;
  transition: ${({ theme }) => theme.transitions.fast};
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  user-select: ${({ $clickable }) => $clickable ? 'none' : 'auto'};

  &:hover {
  background: ${({ theme }) => theme.colors.hoverRow};

    .acciones-flotantes {
      display: flex;
    }
  }

  &:nth-child(even) {
    background: ${({ theme }) => theme.colors.rowAlternate};

    &:hover {
      background: ${({ theme }) => theme.colors.hoverRow};
    }
  }

  ${({ $variant, theme }) => $variant === 'rows' && `
    transition-property: background-color;
    transition-duration: 0.15s;
    transition-timing-function: ease-in-out;

    &:nth-child(even) {
      background: transparent;

      &:hover {
        background: ${theme.colors.surfaceHover};
      }
    }

    &:hover {
      background: ${theme.colors.surfaceHover};
    }
  `}
`;

// CELDA DE TABLA
const TableCell = styled.td`
  padding: 8px 12px; /* Padding reducido para mayor compacidad */
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtSecondary};
  font-size: 13px; /* Fuente más pequeña */
  vertical-align: middle;
  white-space: ${({ $allowWrap }) => ($allowWrap ? 'normal' : 'nowrap')};
  overflow: ${({ $allowWrap }) => ($allowWrap ? 'visible' : 'hidden')};
  text-overflow: ${({ $allowWrap }) => ($allowWrap ? 'clip' : 'ellipsis')};
  text-align: ${({ $align }) => $align || 'left'};
  /* Para columnas con ancho fijo (p.ej. Acciones), permitir override */
  ${({ $width }) => $width ? `width: ${$width}; min-width: ${$width}; max-width: ${$width};` : ''}

  ${({ $variant, theme }) => $variant === 'rows' && `
    padding: 7px 10px;
    font-size: 12px;
    border-bottom: 1px solid ${theme.colors.borderLight};
  `}
`;

// ACCIONES FLOTANTES (EDITAR/ELIMINAR)
const AccionesFlotantes = styled.div`
  display: none;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  gap: 0.5rem;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
`;

const ActionButton = SharedActionButton;

// ESTADO VACÍO
const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${({ theme }) => theme.colors.txtSecondary};
  
  i {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  h4 {
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme.colors.txtPrincipal};
  }
  
  p {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

// LOADING STATE
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: ${({ theme }) => theme.colors.txtSecondary};
  
  i {
    margin-right: 0.5rem;
    animation: ${spin} 1s linear infinite;
  }
`;

// COMPONENTE TABLE PRINCIPAL
const Table = ({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = "No hay datos para mostrar",
  onSort,
  sortColumn,
  sortDirection,
  onRowClick,
  onEdit,
  onDelete,
  showActions = true,
  maxHeight,
  noExtraBottomSpace = false,
  resizableColumns = false,
  columnSizingKey,
  horizontalScroll = false,
  tableVisualVariant = 'grid' // 'grid' (clásica) | 'rows' (filas diferenciadas, igual lenguaje que ListaUnificada)
}) => {
  const [columnWidths, setColumnWidths] = useState({});
  const resizeStateRef = useRef({ columnKey: null, startX: 0, startWidth: 0 });
  const justResizedRef = useRef(false);

  // Render incremental: con miles de filas en memoria, el DOM solo pinta las
  // primeras N y va sumando a medida que el usuario se acerca al final del
  // scroll (IntersectionObserver). Los datos completos siguen en memoria:
  // filtros, orden y exportaciones no pierden nada — solo se limita el DOM.
  const FILAS_INICIALES = 150;
  const FILAS_INCREMENTO = 150;
  const [visibleCount, setVisibleCount] = useState(FILAS_INICIALES);
  const scrollWrapperRef = useRef(null);
  const sentinelRef = useRef(null);
  const totalRows = Array.isArray(data) ? data.length : 0;
  const hasMoreRows = totalRows > visibleCount;

  // Ancho mínimo total de la tabla: suma del ancho de cada columna (el
  // redimensionado por el usuario, el explícito, o un mínimo por defecto) más
  // la columna de acciones. Al fijarlo como min-width de <table>, cuando las
  // columnas visibles no entran en el contenedor la tabla no se comprime para
  // forzarlas a caber (lo que tapaba columnas como "Acciones") — crece y
  // dispara el scroll horizontal que ya habilita TableScrollWrapper.
  const COLUMN_MIN_WIDTH = 90;
  const parseWidthPx = (value, fallback) => {
    if (!value) return fallback;
    const parsed = parseInt(String(value).replace('px', ''), 10);
    return Number.isFinite(parsed) ? Math.max(parsed, 50) : fallback;
  };
  const tableMinWidth = columns.reduce((sum, column) => {
    const width = columnWidths[column.key] || column.width;
    return sum + parseWidthPx(width, COLUMN_MIN_WIDTH);
  }, 0) + (showActions ? 110 : 0);

  useEffect(() => {
    if (!hasMoreRows) return undefined;
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // Sin soporte de observer: mostrar todo (comportamiento previo)
      setVisibleCount(totalRows);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisibleCount((count) => count + FILAS_INCREMENTO);
      }
    }, { root: scrollWrapperRef.current || null, rootMargin: '600px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMoreRows, totalRows]);

  useEffect(() => {
    if (!columnSizingKey) return;
    try {
      const saved = localStorage.getItem(`table_widths:${columnSizingKey}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') setColumnWidths(parsed);
      }
    } catch {
      // Ignorar errores de lectura de almacenamiento local
    }
  }, [columnSizingKey]);

  useEffect(() => {
    if (!columnSizingKey) return;
    try {
      localStorage.setItem(`table_widths:${columnSizingKey}`, JSON.stringify(columnWidths));
    } catch {
      // Ignorar errores de escritura de almacenamiento local
    }
  }, [columnWidths, columnSizingKey]);

  // Manejar ordenamiento
  const handleSort = (columnKey) => {
    if (justResizedRef.current) return;
    const column = columns.find(col => col.key === columnKey);
    if (!column?.sortable) return;
    
    // Usar el campo sortKey si está definido, sino usar key
    const sortField = column.sortKey || column.key;
    
    let newDirection = 'asc';
    if (sortColumn === sortField && sortDirection === 'asc') {
      newDirection = 'desc';
    }
    
    if (onSort) {
      onSort(sortField, newDirection);
    }
  };

  const handleResizeStart = (e, columnKey, side = 'right') => {
    if (!resizableColumns) return;
    e.preventDefault();
    e.stopPropagation();

    const th = e.currentTarget.closest('th');
    if (!th) return;

    // Obtener el TH a redimensionar según el side
    let resizeTh;
    if (side === 'left') {
      resizeTh = th.previousElementSibling;
    } else {
      resizeTh = th;
    }
    
    if (!resizeTh) return;

    // Realizar resize sobre th.previousElementSibling cuando es 'left', sino sobre el actual
    const actualColumnKey = side === 'left' 
      ? columns[columns.findIndex(c => c.key === columnKey) - 1]?.key 
      : columnKey;
    
    if (!actualColumnKey) return;

    const rect = resizeTh.getBoundingClientRect();

    resizeStateRef.current = {
      columnKey: actualColumnKey,
      startX: e.clientX,
      startWidth: rect.width,
    };

    const onMouseMove = (moveEvent) => {
      const { columnKey, startX, startWidth } = resizeStateRef.current;
      if (!columnKey) return;
      
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.max(50, Math.round(startWidth + delta)); // Mínimo 50px para poder achicarse
      
      setColumnWidths(prev => ({
        ...prev,
        [columnKey]: `${newWidth}px`,
      }));
      justResizedRef.current = true;
    };

    const onMouseUp = () => {
      resizeStateRef.current = { columnKey: null, startX: 0, startWidth: 0 };
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setTimeout(() => { justResizedRef.current = false; }, 120);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  // Renderizar celda con formato personalizado
  const { formatDateDisplay } = require('../../utils/dateUtils');
  const formatFechaLocal = (v) => formatDateDisplay(v);
  const renderCell = (item, column) => {
    if (column.render) {
      return column.render(item[column.key], item);
    }
    
    const value = item[column.key];
    
    // Formatear fechas
    if (column.type === 'date' && value) {
      return formatFechaLocal(value);
    }
    
    // Formatear números
    if (column.type === 'number' && value !== null && value !== undefined) {
      return value.toLocaleString('es-AR');
    }
    
    return value || '-';
  };

  const getCellTitle = (item, column) => {
    if (typeof column.cellTitle === 'function') {
      const custom = column.cellTitle(item[column.key], item);
      if (custom === null || custom === undefined || custom === '') return undefined;
      return String(custom);
    }

    if (column.render) {
      return undefined;
    }

    const value = item[column.key];
    if (value === null || value === undefined || value === '') return '-';

    if (column.type === 'date') {
      return String(formatFechaLocal(value));
    }

    if (column.type === 'number') {
      try {
        return String(value.toLocaleString('es-AR'));
      } catch {
        return String(value);
      }
    }

    return String(value);
  };

  // Estados de loading y vacío
  if (loading) {
    return (
      <TableContainer $variant={tableVisualVariant}>
        <LoadingContainer>
          <i className="bi bi-arrow-repeat"></i>
          Cargando datos...
        </LoadingContainer>
      </TableContainer>
    );
  }

  if (!data || data.length === 0) {
    return (
      <TableContainer $variant={tableVisualVariant}>
        <EmptyState>
          <i className="bi bi-inbox"></i>
          <h4>Sin datos</h4>
          <p>{emptyMessage}</p>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer $variant={tableVisualVariant}>
  <TableScrollWrapper ref={scrollWrapperRef} $maxHeight={maxHeight} $noExtraBottomSpace={noExtraBottomSpace} $horizontalScroll={horizontalScroll}>
        <StyledTable $horizontalScroll={horizontalScroll} $minWidth={horizontalScroll ? tableMinWidth : undefined}>
          <TableHeader $variant={tableVisualVariant}>
            <tr>
              {columns.map((column, index) => (
                (() => {
                  const effectiveWidth = columnWidths[column.key] || column.width;
                  return (
                <TableHeaderCell
                  key={column.key}
                  $sortable={column.sortable}
                  $sortDirection={sortColumn === (column.sortKey || column.key) ? sortDirection : null}
                  $width={effectiveWidth}
                  $align={column.headerAlign || column.align}
                  $variant={tableVisualVariant}
                  onClick={() => handleSort(column.key)}
                >
                  {column.title || column.label}
                  {resizableColumns && !column.noResize && index > 0 && (
                    <LeftResizeHandle onMouseDown={(e) => handleResizeStart(e, column.key, 'left')} />
                  )}
                  {resizableColumns && !column.noResize && (
                    <ResizeHandle onMouseDown={(e) => handleResizeStart(e, column.key, 'right')} />
                  )}
                </TableHeaderCell>
                  );
                })()
              ))}
              {/* Dummy column para que la última columna sea achicable */}
              <TableHeaderCell style={{ width: 1, minWidth: 1, maxWidth: 1, padding: 0, border: 'none', background: 'transparent' }} />
              {showActions && <TableHeaderCell $variant={tableVisualVariant}>Acciones</TableHeaderCell>}
            </tr>
          </TableHeader>
          <TableBody>
            {data.slice(0, visibleCount).map((item, index) => (
              <TableRow
                key={item.id || index}
                onClick={onRowClick ? (e) => onRowClick(item, index, e) : undefined}
                $clickable={!!onRowClick}
                $variant={tableVisualVariant}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    $width={columnWidths[column.key] || column.width}
                    $align={column.align}
                    $allowWrap={Boolean(column.allowWrap)}
                    $variant={tableVisualVariant}
                    title={getCellTitle(item, column)}
                  >
                    {renderCell(item, column)}
                  </TableCell>
                ))}

                {/* Dummy cell invisible para que la última columna sea achicable */}
                <TableCell style={{ width: 1, minWidth: 1, maxWidth: 1, padding: 0, border: 'none', background: 'transparent' }} />
                {showActions && (
                  <TableCell $variant={tableVisualVariant}>
                    <AccionesFlotantes className="acciones-flotantes">
                      {onEdit && (
                        <ActionButton
                          $variant="edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(item);
                          }}
                          title="Editar"
                        >
                          <i className="bi bi-pencil"></i>
                        </ActionButton>
                      )}
                      {onDelete && (
                        <ActionButton
                          $variant="delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(item);
                          }}
                          title="Eliminar"
                        >
                          <i className="bi bi-trash"></i>
                        </ActionButton>
                      )}
                    </AccionesFlotantes>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {hasMoreRows && (
              <tr ref={sentinelRef}>
                <SentinelCell colSpan={columns.length + 1 + (showActions ? 1 : 0)}>
                  Mostrando {Math.min(visibleCount, totalRows)} de {totalRows} filas — deslizá para ver más
                </SentinelCell>
              </tr>
            )}
          </TableBody>
        </StyledTable>
      </TableScrollWrapper>
    </TableContainer>
  );
};

export default Table;